import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import {
  PageOrientation,
  PageSize,
  PoBlockType,
  PoFontWeight,
  PoLineItemColumnType,
  PoShipmentColumnType,
} from "./po-block-types";

// --- Base schemas ---

const baseSectionSchema = z.object({
  blockId: z.string(),
  sectionPadding: z.object({
    top: z.number(),
    right: z.number(),
    bottom: z.number(),
    left: z.number(),
  }),
  sectionColor: z.string().nullish(),
});

const fontSectionSchema = baseSectionSchema.extend({
  fontFamily: z.string().nullish(),
  fontSize: z.number().nullish(),
  textColor: z.string().nullish(),
  fontWeight: createEnum(PoFontWeight, { enumName: "PoFontWeight" }).nullish(),
});

const addressSectionSchema = fontSectionSchema.extend({
  showHeader: z.boolean().nullish(),
  header: z.string().nullish(),
  showName: z.boolean().nullish(),
  boldName: z.boolean().nullish(),
  showStreet: z.boolean().nullish(),
  showCity: z.boolean().nullish(),
  showState: z.boolean().nullish(),
  showPostalCode: z.boolean().nullish(),
  showCountry: z.boolean().nullish(),
  showPhone: z.boolean().nullish(),
  showEmail: z.boolean().nullish(),
});

// --- Company blocks ---

const companyNameSchema = fontSectionSchema.extend({
  type: z.literal(PoBlockType.COMPANY_NAME),
});

const companyLogoSchema = baseSectionSchema.extend({
  type: z.literal(PoBlockType.COMPANY_LOGO),
  imageUrl: z.string().nullish(),
  maxHeight: z.number().nullish(),
});

const companyAddressSchema = addressSectionSchema.extend({
  type: z.literal(PoBlockType.COMPANY_ADDRESS),
});

const companyContactSchema = fontSectionSchema.extend({
  type: z.literal(PoBlockType.COMPANY_CONTACT),
  showPhone: z.boolean(),
  showEmail: z.boolean(),
});

// --- Supplier blocks ---

const supplierNameSchema = fontSectionSchema.extend({
  type: z.literal(PoBlockType.SUPPLIER_NAME),
});

const supplierAddressSchema = addressSectionSchema.extend({
  type: z.literal(PoBlockType.SUPPLIER_ADDRESS),
});

const supplierContactSchema = fontSectionSchema.extend({
  type: z.literal(PoBlockType.SUPPLIER_CONTACT),
  showPhone: z.boolean(),
  showEmail: z.boolean(),
});

// --- PO header blocks ---

const poNumberSchema = fontSectionSchema.extend({
  type: z.literal(PoBlockType.PO_NUMBER),
  labelPrefix: z.string().nullish(),
});

const creationDateSchema = fontSectionSchema.extend({
  type: z.literal(PoBlockType.CREATION_DATE),
  dateFormat: z.string().nullish(),
});

const expectedDeliveryDateSchema = fontSectionSchema.extend({
  type: z.literal(PoBlockType.EXPECTED_DELIVERY_DATE),
  dateFormat: z.string().nullish(),
});

const paymentTermsSchema = fontSectionSchema.extend({
  type: z.literal(PoBlockType.PAYMENT_TERMS),
});

const shipToAddressSchema = addressSectionSchema.extend({
  type: z.literal(PoBlockType.SHIP_TO_ADDRESS),
});

// --- Line items ---

const lineItemTableColumnSchema = z.object({
  type: createEnum(PoLineItemColumnType, { enumName: "PoLineItemColumnType" }),
  enabled: z.boolean(),
  title: z.string().nullish(),
});

const lineItemsTableSchema = fontSectionSchema.extend({
  type: z.literal(PoBlockType.LINE_ITEMS_TABLE),
  tableColumns: z.array(lineItemTableColumnSchema),
  includeHeader: z.boolean().nullish(),
  tableBorderColor: z.string().nullish(),
  tableBorderWidth: z.number().nullish(),
  rowHeight: z.number().nullish(),
});

// --- Shipments table ---

const shipmentTableColumnSchema = z.object({
  type: createEnum(PoShipmentColumnType, { enumName: "PoShipmentColumnType" }),
  enabled: z.boolean(),
  title: z.string().nullish(),
});

const shipmentsTableSchema = fontSectionSchema.extend({
  type: z.literal(PoBlockType.SHIPMENTS_TABLE),
  tableColumns: z.array(shipmentTableColumnSchema),
  includeHeader: z.boolean().nullish(),
  tableBorderColor: z.string().nullish(),
  tableBorderWidth: z.number().nullish(),
  rowHeight: z.number().nullish(),
});

// --- Summary ---

const poSummarySchema = fontSectionSchema.extend({
  type: z.literal(PoBlockType.PO_SUMMARY),
  showSubtotal: z.boolean(),
  showTax: z.boolean(),
  showShipping: z.boolean(),
  showTotal: z.boolean(),
  showCurrency: z.boolean(),
});

// --- Notes ---

const notesFieldSchema = fontSectionSchema.extend({
  type: z.literal(PoBlockType.NOTES_FIELD),
  showHeader: z.boolean().nullish(),
  header: z.string().nullish(),
});

// --- Generic blocks ---

const textSchema = baseSectionSchema.extend({
  type: z.literal(PoBlockType.TEXT),
  textColor: z.string(),
  fontSize: z.number(),
  fontFamily: z.string(),
  linkColor: z.string(),
  text: z.string(),
});

const imageSchema = baseSectionSchema.extend({
  type: z.literal(PoBlockType.IMAGE),
  imageUrl: z.string(),
  showCaption: z.boolean(),
  maxHeight: z.number().nullish(),
  caption: z.string().nullish(),
  altText: z.string().nullish(),
});

const spacerSchema = baseSectionSchema.extend({
  type: z.literal(PoBlockType.SPACER),
  height: z.number(),
});

const lineSchema = baseSectionSchema.extend({
  type: z.literal(PoBlockType.LINE),
  color: z.string(),
});

// --- Section union ---

const sectionWithoutColumnSchema = z.discriminatedUnion("type", [
  companyNameSchema,
  companyLogoSchema,
  companyAddressSchema,
  companyContactSchema,
  supplierNameSchema,
  supplierAddressSchema,
  supplierContactSchema,
  poNumberSchema,
  creationDateSchema,
  expectedDeliveryDateSchema,
  paymentTermsSchema,
  shipToAddressSchema,
  lineItemsTableSchema,
  shipmentsTableSchema,
  poSummarySchema,
  notesFieldSchema,
  textSchema,
  imageSchema,
  spacerSchema,
  lineSchema,
]);

const columnSchema = baseSectionSchema.extend({
  type: z.literal(PoBlockType.COLUMN),
  columns: z.array(z.lazy(() => sectionWithoutColumnSchema)),
  columnCount: z.number(),
  gap: z.number(),
});

const poSectionSchema = z.discriminatedUnion("type", [
  ...sectionWithoutColumnSchema._def.options,
  columnSchema,
]);

export type PoSectionZod = z.infer<typeof poSectionSchema>;

// --- Page layout ---

const poPageLayoutSchema_ = z.object({
  orientation: createEnum(PageOrientation, { enumName: "PageOrientation" }),
  size: createEnum(PageSize, { enumName: "PageSize" }),
});
export const poPageLayoutSchema: z.ZodType<PoTemplatePageLayout> =
  poPageLayoutSchema_;
export interface PoTemplatePageLayout
  extends z.infer<typeof poPageLayoutSchema_> {}

// --- Template schema ---

const poTemplateSchema_ = z.object({
  _id: z.string(),
  name: z.string(),
  accountId: z.string(),
  sections: z.array(poSectionSchema),
  pageLayout: poPageLayoutSchema as z.ZodType<PoTemplatePageLayout>,
  isDefault: z.boolean().optional(),
  updatedAt: z.string(),
  deletedAt: z.string().optional(),
});
export const poTemplateSchema: z.ZodType<PoTemplate> = poTemplateSchema_;
export interface PoTemplate extends z.infer<typeof poTemplateSchema_> {}
